import React, { useEffect, useRef, useState } from 'react';
import { format, isToday, isBefore, isAfter } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { CalendarEvent, CalendarPermissions } from '../../types';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import CalendarEmptyDay from './CalendarEmptyDay';
import { cn } from '@/lib/utils';
import { Copy, Plus, Trash2, CopyCheck, Moon, Sun, Calendar, Clock, ArrowLeftRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CalendarEventItem from './CalendarEventItem';
import { 
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import { ScrollArea } from '@/components/ui/scroll-area';

interface CalendarRowViewProps {
  daySchedules: { date: Date; events: CalendarEvent[] }[];
  handleDayClick: (date: Date) => void;
  handleEventClick: (event: CalendarEvent, e: React.MouseEvent, isMultiSelectMode?: boolean) => void;
  onDuplicateEvent: (event: CalendarEvent) => void;
  onDeleteEvent: (event: CalendarEvent) => void;
  onCopyShift: (event: CalendarEvent) => void;
  onCopyShiftWithoutGuards: (event: CalendarEvent) => void;
  onPasteShift: (targetDate: Date) => Promise<CalendarEvent | null>;
  onCopyDay?: (date: Date, events: CalendarEvent[], withGuards?: boolean) => void;
  onPasteDay?: (date: Date) => Promise<CalendarEvent[]>;
  onDeleteDay?: (date: Date, events: CalendarEvent[]) => void;
  onDayRightClick?: (e: React.MouseEvent, date: Date) => void;
  onMarkForSwap?: (event: CalendarEvent) => void;
  onSwapWithMarked?: (event: CalendarEvent) => void;
  onCancelSwap?: () => void;
  markedShiftId?: string | null;
  permissions?: CalendarPermissions;
  titleColors: Record<string, { backgroundColor: string; textColor: string }>;
  isMultiSelectMode?: boolean;
  selectedEvents?: Set<string>;
  onEventSelect?: (eventId: string) => void;
  isMonthView?: boolean;
  showDuration?: boolean;
  monthViewColumnWidth?: string;
  eventsWithMultipleShifts?: Set<string>;
  isTotalWorkView?: boolean;
  isPerJobView?: boolean;
  calendarZoom?: number;
}

const CalendarRowView: React.FC<CalendarRowViewProps> = ({
  daySchedules,
  handleDayClick,
  handleEventClick,
  onDuplicateEvent,
  onDeleteEvent,
  onCopyShift,
  onCopyShiftWithoutGuards,
  onPasteShift,
  onCopyDay,
  onPasteDay,
  onDeleteDay,
  onDayRightClick,
  onMarkForSwap,
  onSwapWithMarked,
  onCancelSwap,
  markedShiftId,
  permissions = { canCreate: false, canUpdate: false, canDelete: false },
  titleColors,
  isMultiSelectMode = false,
  selectedEvents = new Set(),
  onEventSelect,
  isMonthView = false,
  showDuration = true,
  monthViewColumnWidth,
  eventsWithMultipleShifts = new Set(),
  isTotalWorkView = false,
  isPerJobView = false,
  calendarZoom = 0.9
}) => {
  console.log('CalendarRowView calendarZoom:', calendarZoom);
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  
  // Responsive title column width with better sizing
  const titleColumnWidth = isMobile ? "120px" : "160px";
  
  // Minimum height for a single shift tab
  const MIN_SHIFT_HEIGHT = 90; // Base minimum height for shift tabs
  
  // Calculate dynamic row height based on shifts
  const calculateRowHeight = (eventsForTitle: Record<string, CalendarEvent[]>) => {
    let maxShiftsInDay = 0;
    
    // Find the maximum number of shifts in any day for this title
    Object.values(eventsForTitle).forEach(events => {
      if (events.length > maxShiftsInDay) {
        maxShiftsInDay = events.length;
      }
    });
    
    if (maxShiftsInDay === 0) {
      return MIN_SHIFT_HEIGHT;
    }
    
    // Calculate height: sum of minimum heights for all shifts + 10%
    const totalShiftHeight = maxShiftsInDay * MIN_SHIFT_HEIGHT;
    return Math.ceil(totalShiftHeight * 1.1);
  };
  
  // For mobile view, calculate day column width to show 2 days in viewport
  const getDayColumnWidth = () => {
    if (isMobile && !isMonthView) {
      // Calculate width to show 2 days in viewport with some padding
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
      const titleWidth = 120; // mobile title column width
      const availableWidth = viewportWidth - titleWidth - 32; // subtract padding
      return `${Math.floor(availableWidth / 2)}px`;
    }
    
    if (isMonthView) {
      return monthViewColumnWidth || (isMobile ? "150px" : "180px");
    }
    
    return "auto"; // Flexible width for desktop week view
  };
  
  const dayColumnWidth = getDayColumnWidth();
  
  // Synchronize scroll between header and content
  useEffect(() => {
    const headerElement = headerRef.current;
    const contentElement = contentRef.current;
    
    if (!headerElement || !contentElement) return;
    
    const handleHeaderScroll = () => {
      if (contentElement) {
        contentElement.scrollLeft = headerElement.scrollLeft;
      }
    };
    
    const handleContentScroll = () => {
      if (headerElement) {
        headerElement.scrollLeft = contentElement.scrollLeft;
      }
    };
    
    headerElement.addEventListener('scroll', handleHeaderScroll);
    contentElement.addEventListener('scroll', handleContentScroll);
    
    return () => {
      headerElement.removeEventListener('scroll', handleHeaderScroll);
      contentElement.removeEventListener('scroll', handleContentScroll);
    };
  }, []);
  
  // Vibrant color palette for shift tabs
  const getVibrantShiftColor = (title: string) => {
    const vibrantColors = [
      { bg: '#8B5A2B', text: '#FFFFFF' }, // Brown
      { bg: '#6A4C93', text: '#FFFFFF' }, // Purple
      { bg: '#0F766E', text: '#FFFFFF' }, // Teal
      { bg: '#1D4ED8', text: '#FFFFFF' }, // Blue
      { bg: '#B91C1C', text: '#FFFFFF' }, // Dark red
      { bg: '#B5651D', text: '#FFFFFF' }, // Orange
      { bg: '#8B5CF6', text: '#FFFFFF' }, // Light purple
      { bg: '#14B8A6', text: '#FFFFFF' }, // Light teal
      { bg: '#2563EB', text: '#FFFFFF' }, // Medium blue
      { bg: '#DC2626', text: '#FFFFFF' }, // Red
      { bg: '#D2691E', text: '#FFFFFF' }, // Dark orange
      { bg: '#9333EA', text: '#FFFFFF' }, // Violet
      { bg: '#059669', text: '#FFFFFF' }, // Emerald
      { bg: '#3B82F6', text: '#FFFFFF' }, // Sky blue
      { bg: '#7F1D1D', text: '#FFFFFF' }, // Dark burgundy
    ];

    // Generate a consistent color based on title hash
    let hash = 0;
    for (let i = 0; i < title.length; i++) {
      const char = title.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    const colorIndex = Math.abs(hash) % vibrantColors.length;
    return vibrantColors[colorIndex];
  };

  // Helper function to format name with last name first (the data is already in correct format)
  const formatNameWithLastNameFirst = (name: string) => {
    // The guard data already comes in "LAST_NAME FIRST_NAME" format, so just return as is
    return name;
  };
  
  const getEventGuardNames = (event: CalendarEvent) => {
    console.log('CalendarRowView - Getting guard names for event:', {
      eventId: event.id,
      guard_names: event.guard_names,
      assigned_guards: event.assigned_guards
    });

    // In total work view, show team name instead of guard names
    if (isTotalWorkView && event.team_name) {
      return String(event.team_name);
    }
    
    // Check if we have guards data from global window object
    const guards = (window as any)?.guards || [];
    
    // Always use the guards data from global window object for proper formatting
    // Skip event.guard_names as it may have old format names
    
    // Try assigned_guards if guard_names is not available
    if (event.assigned_guards && Array.isArray(event.assigned_guards) && event.assigned_guards.length > 0) {
      console.log('CalendarRowView - Processing assigned_guards:', event.assigned_guards);
      
      // Check if assigned_guards contains guard objects with name properties
      const firstGuard = event.assigned_guards[0];
      if (firstGuard && typeof firstGuard === 'object' && firstGuard !== null) {
        const guardObj = firstGuard as Record<string, any>;
        if ('name' in guardObj || 'full_name' in guardObj || 'first_name' in guardObj || 'last_name' in guardObj) {
          const guardNames = event.assigned_guards
            .map((guard: any) => {
              if (guard && typeof guard === 'object') {
                // Try different name combinations
                if (guard.full_name) {
                  return formatNameWithLastNameFirst(guard.full_name);
                }
                
                if (guard.first_name && guard.last_name) {
                  return `${guard.last_name}, ${guard.first_name}`;
                }
                
                if (guard.name) {
                  return formatNameWithLastNameFirst(guard.name);
                }
                
                return guard.first_name || guard.last_name || null;
              }
              return null;
            })
            .filter(Boolean);
          
          if (guardNames.length > 0) {
            return guardNames.join(', ');
          }
        }
      }
      
      // If assigned_guards contains just IDs, try to match with guards data
      if (guards && guards.length > 0) {
        console.log('CalendarRowView - Matching guard IDs with global guards data');
        const guardNames = event.assigned_guards
          .map((guardId: string) => {
            if (typeof guardId === 'string') {
              const guard = guards.find((g: any) => g.id === guardId);
              console.log(`CalendarRowView - Looking for guard ID ${guardId}:`, guard);
              if (guard) {
                if (guard.full_name) {
                  return formatNameWithLastNameFirst(guard.full_name);
                }
                if (guard.first_name && guard.last_name) {
                  return `${guard.last_name}, ${guard.first_name}`;
                }
                if (guard.name) {
                  return formatNameWithLastNameFirst(guard.name);
                }
                return guard.first_name || guard.last_name || `Guard ${guardId.slice(0, 8)}`;
              }
            }
            return null;
          })
          .filter(Boolean);
        
        if (guardNames.length > 0) {
          return guardNames.join(', ');
        }
      }
      
      // If we still don't have names, show count only if we actually have assigned guards
      return `${event.assigned_guards.length} guard(s)`;
    }
    
    return 'No Personnel Assigned';
  };
  
  // Function to get the display text for the shift tab
  const getShiftDisplayText = (event: CalendarEvent) => {
    if (isPerJobView) {
      // In per-job view, show the post/job title
      return event.title || 'No post assigned';
    } else if (isTotalWorkView && event.team_name) {
      // In total work view, show team name
      return String(event.team_name);
    } else {
      // In standard view, show guard names
      return getEventGuardNames(event);
    }
  };
  
  // Group events by title
  const eventsByTitle = React.useMemo(() => {
    const groupedEvents: Record<string, Record<string, CalendarEvent[]>> = {};
    
    // Get unique titles from all events
    const allEvents = daySchedules.flatMap(day => day.events);

    // Helper: detect special (non-working/leave) titles that should be placed last
    const isSpecialTitle = (title: string) => {
      const t = (title || '').toUpperCase();
      return (
        t.includes('ΡΕΠΟ') ||
        t.includes('REPO') ||
        t.includes('ΜΗ ΕΡΓΑΣΙΑ') ||
        t.includes('ΑΔΕΙΑ') || // any leave in Greek (e.g., ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ, ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ)
        t.includes('LEAVE') ||
        t.includes('HOLIDAY') ||
        t.includes('VACATION') ||
        t.includes('SICK')
      );
    };

    const titleSet = new Set(allEvents.map(event => event.title));
    const allTitles = Array.from(titleSet);

    // Sort titles: normal job posts first (A–Z), then special titles (A–Z)
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const normalTitles = allTitles.filter(t => !isSpecialTitle(t)).sort(collator.compare);
    const specialTitles = allTitles.filter(isSpecialTitle).sort(collator.compare);
    const uniqueTitles = [...normalTitles, ...specialTitles];
    
    // Initialize structure
    uniqueTitles.forEach(title => {
      groupedEvents[title] = {};
      daySchedules.forEach(day => {
        const dateKey = day.date.toISOString();
        groupedEvents[title][dateKey] = [];
      });
    });
    
    // Populate with events
    daySchedules.forEach(day => {
      const dateKey = day.date.toISOString();
      day.events.forEach(event => {
        groupedEvents[event.title][dateKey].push(event);
      });
    });
    
    return { groupedEvents, uniqueTitles };
  }, [daySchedules]);
  
  const { groupedEvents, uniqueTitles } = eventsByTitle;
  
  // If there are no events at all, show a message and an add button
  const allEvents = daySchedules.flatMap(day => day.events);
  const hasNoEvents = allEvents.length === 0;
  
  const isDayShift = (start: Date, end: Date) => {
    const dayStart = new Date(start);
    dayStart.setHours(6, 0, 0, 0);
    
    const dayEnd = new Date(start);
    dayEnd.setHours(18, 0, 0, 0);
    
    return (isAfter(start, dayStart) || start.getTime() === dayStart.getTime()) && 
           (isBefore(start, dayEnd) || start.getTime() === dayEnd.getTime());
  };
  
  const getShiftIcon = (event: CalendarEvent) => {
    const startDate = fromLocalISOString(event.start_date);
    const endDate = fromLocalISOString(event.end_date);
    
    if (event.title.toUpperCase().includes('ΡΕΠΟ') || 
        event.title.toUpperCase().includes('REPO') ||
        event.title.toLowerCase().includes('holiday') ||
        event.title.toLowerCase().includes('vacation') ||
        event.title.includes('ΑΔΕΙΑ')) {
      return <Calendar className="h-3 w-3 mr-1 text-black" />;
    } else if (isDayShift(startDate, endDate)) {
      return <Sun className="h-3 w-3 mr-1 text-black" />;
    } else {
      return <Moon className="h-3 w-3 mr-1 text-black" />;
    }
  };
  
  // Calculate total width needed for all days in mobile and month view
  const totalDaysWidth = (isMobile && !isMonthView) || isMonthView 
    ? (daySchedules.length * parseInt(dayColumnWidth as string)) 
    : "100%";
  
  // Calculate column flex or width based on view type
  const getColumnStyle = () => {
    if ((isMobile && !isMonthView) || isMonthView) {
      return { 
        width: dayColumnWidth,
        minWidth: dayColumnWidth,
        maxWidth: dayColumnWidth,
        flexShrink: 0
      };
    } else {
      return { 
        flex: "1 0 0%",
        minWidth: "0"
      };
    }
  };
  
  const calculateShiftDuration = (event: CalendarEvent) => {
    const startDate = fromLocalISOString(event.start_date);
    const endDate = fromLocalISOString(event.end_date);
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    
    // Format to fixed 1 decimal place if not a whole number
    return durationHours % 1 === 0 
      ? `${durationHours}hrs` 
      : `${durationHours.toFixed(1)}hrs`;
  };

  // Helper function to handle day clicks safely
  const handleSafeDayClick = (date: Date, e?: React.MouseEvent) => {
    if (contextMenuOpen) return; // Don't trigger day click if context menu was just used
    if (e && e.defaultPrevented) return; // Don't trigger if event was prevented
    handleDayClick(date);
  };

  const handleDayContextMenuAction = async (action: string, date: Date, events?: CalendarEvent[]) => {
    // Prevent context menu from triggering day click
    setContextMenuOpen(true);
    
    try {
      switch (action) {
        case 'addShift':
          handleDayClick(date);
          break;
        case 'pasteShift':
          if (onPasteShift) {
            await onPasteShift(date);
          }
          break;
        case 'copyDayWithGuards':
          if (onCopyDay && events) {
            onCopyDay(date, events, true);
          }
          break;
        case 'copyDayWithoutGuards':
          if (onCopyDay && events) {
            onCopyDay(date, events, false);
          }
          break;
        case 'pasteDay':
          if (onPasteDay) {
            await onPasteDay(date);
          }
          break;
        case 'deleteDay':
          if (onDeleteDay && events) {
            onDeleteDay(date, events);
          }
          break;
      }
    } finally {
      // Reset context menu state after a short delay
      setTimeout(() => setContextMenuOpen(false), 100);
    }
  };
  
  if (hasNoEvents && permissions.canCreate) {
    return (
      <div className="flex flex-col space-y-4 w-full">
        {/* Days header row with integrated day and date display */}
        <div className="flex border-b border-border dark:border-gray-700 mb-2 overflow-hidden w-full">
          <div 
            className="z-10 bg-card dark:bg-background border-r border-border dark:border-gray-700 p-2 font-medium"
            style={{ 
              width: titleColumnWidth,
              minWidth: titleColumnWidth,
              flexShrink: 0,
              position: "sticky",
              left: 0
            }}
          ></div>
          <div
            ref={headerRef}
            className="overflow-x-auto flex-1 w-full"
            style={{ width: `calc(100% - ${titleColumnWidth})` }}
          >
            <div 
              className="flex" 
              style={(isMobile && !isMonthView) || isMonthView ? { minWidth: `${totalDaysWidth}px` } : { width: "100%" }}
            >
              {daySchedules.map((daySchedule, index) => (
                <div 
                  key={`day-header-${daySchedule.date.toISOString()}`} 
                  className={cn(
                    "text-center py-2 font-medium text-sm border-r w-full",
                    isToday(daySchedule.date) && "bg-primary/10",
                    index === 0 && "rounded-tl-lg",
                    index === daySchedules.length - 1 && "rounded-tr-lg last:border-r-0"
                  )}
                  style={getColumnStyle()}
                >
                  <div>{format(daySchedule.date, "EEE")}</div>
                  <div className={cn(
                    "text-lg",
                    isToday(daySchedule.date) && "bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto"
                  )}>
                    {format(daySchedule.date, "d")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Empty state with clickable area to add shifts */}
        <div className="h-64 border border-border dark:border-gray-700 rounded-md bg-card p-6 flex flex-col items-center justify-center w-full">
          <p className="text-muted-foreground mb-4 text-center">{t('noShiftsScheduled') || 'No shifts scheduled for this period'}</p>
          {permissions.canCreate && (
            <Button 
              variant="outline" 
              className="flex items-center gap-2" 
              onClick={() => handleDayClick(daySchedules[0].date)}
            >
              <Plus className="h-4 w-4" />
              {t('addShift') || 'Add Shift'}
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full h-full">
      <div className="flex flex-col space-y-4 w-full overflow-hidden">
        {/* Visual indicators for horizontal scroll - show for mobile and month view */}
        {((isMobile && !isMonthView) || isMonthView) && (
          <div className="relative w-full">
            <div className="absolute -right-2 top-1/2 h-12 w-4 bg-gradient-to-l from-background to-transparent pointer-events-none z-10"></div>
            <div className="absolute -left-2 top-1/2 h-12 w-4 bg-gradient-to-r from-background to-transparent pointer-events-none z-10"></div>
          </div>
        )}
        
        {/* Days header row with fixed title column and integrated day/date display */}
        <div className="flex w-full border-b border-border dark:border-gray-700 mb-2 relative overflow-hidden">
          {/* Fixed title column header with responsive text */}
          <div 
            className="z-10 bg-card dark:bg-background border-r border-border dark:border-gray-700 p-2 font-medium" 
            style={{ 
              width: titleColumnWidth,
              minWidth: titleColumnWidth,
              flexShrink: 0,
              position: "sticky",
              left: 0
            }}
          >
            <div className="text-center text-xs sm:text-sm break-words whitespace-normal leading-tight">
              {isPerJobView ? (t('employee') || 'Employee') : (t('title') || 'Title')}
            </div>
          </div>
          
          {/* Scrollable day headers - with integrated day and date display */}
          <div 
            ref={headerRef} 
            className="flex-1 overflow-x-auto"
            style={{ width: `calc(100% - ${titleColumnWidth})` }}
          >
            <div 
              className="flex" 
              style={(isMobile && !isMonthView) || isMonthView ? { minWidth: `${totalDaysWidth}px`, width: `${totalDaysWidth}px` } : { width: "100%" }}
            >
              {daySchedules.map((daySchedule, index) => (
                <ContextMenu key={`day-header-${daySchedule.date.toISOString()}`}>
                  <ContextMenuTrigger asChild>
                    <div 
                      className={cn(
                        "text-center py-2 font-medium text-sm border-r border-border dark:border-gray-700 w-full",
                        isToday(daySchedule.date) && "bg-primary/10",
                        index === 0 && "rounded-tl-lg",
                        index === daySchedules.length - 1 && "rounded-tr-lg last:border-r-0"
                      )}
                      style={getColumnStyle()}
                    >
                      <div>{format(daySchedule.date, "EEE")}</div>
                      <div className={cn(
                        "text-lg",
                        isToday(daySchedule.date) && "bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto"
                      )}>
                        {format(daySchedule.date, "d")}
                      </div>
                    </div>
                  </ContextMenuTrigger>
                  <ContextMenuContent>
                    <ContextMenuItem 
                      onClick={() => handleDayContextMenuAction('addShift', daySchedule.date)}
                      disabled={!permissions.canCreate}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('addShift') || 'Add Shift'}
                    </ContextMenuItem>

                    {onPasteShift && (
                      <ContextMenuItem 
                        onClick={() => handleDayContextMenuAction('pasteShift', daySchedule.date)}
                        disabled={!permissions.canUpdate}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        {t('pasteShift') || 'Paste Shift'}
                      </ContextMenuItem>
                    )}
                    
                    {onCopyDay && daySchedule.events.length > 0 && (
                      <>
                        <ContextMenuSeparator />
                        <ContextMenuItem
                          onClick={() => handleDayContextMenuAction('copyDayWithGuards', daySchedule.date, daySchedule.events)}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          {t('copyDayWithGuards') || 'Copy Day (with guards)'}
                        </ContextMenuItem>
                        <ContextMenuItem
                          onClick={() => handleDayContextMenuAction('copyDayWithoutGuards', daySchedule.date, daySchedule.events)}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          {t('copyDayWithoutGuards') || 'Copy Day (without guards)'}
                        </ContextMenuItem>
                      </>
                    )}
                    
                    {onPasteDay && (
                      <ContextMenuItem
                        onClick={() => handleDayContextMenuAction('pasteDay', daySchedule.date)}
                        disabled={!permissions.canCreate}
                      >
                        <CopyCheck className="h-4 w-4 mr-2" />
                        {t('pasteDay') || 'Paste Day'}
                      </ContextMenuItem>
                    )}
                    
                    {onDeleteDay && daySchedule.events.length > 0 && (
                      <>
                        <ContextMenuSeparator />
                        <ContextMenuItem
                          onClick={() => handleDayContextMenuAction('deleteDay', daySchedule.date, daySchedule.events)}
                          disabled={!permissions.canDelete}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('deleteDay') || 'Delete Day'}
                        </ContextMenuItem>
                      </>
                    )}
                  </ContextMenuContent>
                </ContextMenu>
              ))}
            </div>
          </div>
        </div>
        
        {/* Main calendar body with sticky title column */}
        <div className="flex w-full flex-1 overflow-hidden">
          {/* Fixed titles column with improved responsive text handling */}
          <div 
            className="flex flex-col z-10 bg-card dark:bg-background border-r border-border dark:border-gray-700" 
            style={{ 
              width: titleColumnWidth,
              minWidth: titleColumnWidth,
              flexShrink: 0,
              position: "sticky",
              left: 0
            }}
          >
            {uniqueTitles.map((title) => {
              // Check if this title has any events
              const hasEvents = Object.values(groupedEvents[title] || {}).some(events => events.length > 0);
              if (!hasEvents) return null;
              
              // Calculate dynamic row height
              const rowHeight = calculateRowHeight(groupedEvents[title] || {});
              
                // Use original vibrant colors but allow titleColors override for intensity
                const titleColor = titleColors[title];
                const vibrantColor = getVibrantShiftColor(title);
                
                return (
                  <div 
                    key={`title-column-${title}`} 
                    className="p-2 sm:p-3 border-b border-border dark:border-gray-700 flex items-center justify-center rounded-lg"
                    style={{
                      backgroundColor: titleColor?.backgroundColor || vibrantColor.bg,
                      color: titleColor?.textColor || vibrantColor.text,
                      height: `${rowHeight}px`,
                      fontWeight: 500,
                    }}
                  >
                  <span className="w-full text-center text-xs sm:text-sm leading-tight break-words whitespace-normal hyphens-auto" 
                        style={{ 
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          lineHeight: '1.2'
                        }}>
                    {title}
                  </span>
                </div>
              );
            })}
            
            {/* Add a placeholder slot for "Add New Shift Type" row */}
            {permissions.canCreate && !isMultiSelectMode && (
              <div 
                className="border-b border-border dark:border-gray-700 flex items-center justify-center" 
                style={{ height: '64px' }}
              ></div>
            )}
          </div>
          
          {/* Scrollable content area with proper horizontal scrolling */}
          <div 
            ref={contentRef} 
            className="flex-1 overflow-x-auto"
            style={{ width: `calc(100% - ${titleColumnWidth})` }}
          >
            <div 
              className="flex flex-col" 
              style={(isMobile && !isMonthView) || isMonthView ? { minWidth: `${totalDaysWidth}px`, width: `${totalDaysWidth}px` } : { width: "100%" }}
            >
              {uniqueTitles.map((title) => {
                const eventsForTitle = groupedEvents[title];
                const hasEvents = Object.values(eventsForTitle).some(events => events.length > 0);
                if (!hasEvents) return null;
                
                // Calculate dynamic row height for this title row
                const rowHeight = calculateRowHeight(eventsForTitle);
                
                // Make sure we have a valid color even if not provided
                const titleColor = titleColors[title] || { backgroundColor: '#f5f5f5', textColor: '#000000' };
                
                return (
                  <div 
                    key={`row-${title}`} 
                    className="flex border-b border-border dark:border-gray-700 w-full" 
                    style={{ height: `${rowHeight}px` }}
                  >
                    {/* Day columns with events */}
                    {daySchedules.map((daySchedule, index) => {
                      const dateKey = daySchedule.date.toISOString();
                      const eventsForDay = eventsForTitle[dateKey] || [];
                      
                      return (
                        <ContextMenu key={`day-column-${title}-${dateKey}`}>
                          <ContextMenuTrigger asChild>
                            <div 
                              className={`border-r ${((isMobile && !isMonthView) || isMonthView) ? 'flex-none' : 'flex-1'} ${index === daySchedules.length - 1 ? 'border-r-0' : ''} ${isToday(daySchedule.date) ? 'bg-blue-50/20 dark:bg-blue-900/10' : ''}`}
                              style={getColumnStyle()}
                              onClick={(e) => handleSafeDayClick(daySchedule.date, e)}
                            >
                              <div className="p-2 flex flex-col gap-2 h-full overflow-y-auto">
                                {eventsForDay.length > 0 ? (
                                  eventsForDay.map((event) => {
                                     const hasGuards = event.assigned_guards && event.assigned_guards.length > 0;
                                     const hasMultipleShifts = eventsWithMultipleShifts.has(event.id);
                                     
                                     // Check if shift has multiple employees
                                     const hasMultipleEmployees = () => {
                                       if (event.guard_names && Array.isArray(event.guard_names)) {
                                         return event.guard_names.filter(name => name && typeof name === 'string' && name.trim() !== '').length > 1;
                                       }
                                       if (event.assigned_guards && Array.isArray(event.assigned_guards)) {
                                         return event.assigned_guards.length > 1;
                                       }
                                       return false;
                                     };
                                     const multipleEmployees = hasMultipleEmployees();
                                     
                                     // Get the correct display text based on view type
                                     const displayText = getShiftDisplayText(event);
                                     
                                     // Diagonal line pattern for multiple shifts
                                     const diagonalPattern = hasMultipleShifts ? {
                                       backgroundImage: `repeating-linear-gradient(45deg, 
                                         rgba(139, 92, 246, 0.15), 
                                         rgba(139, 92, 246, 0.15) 5px, 
                                         transparent 5px, 
                                         transparent 15px)`
                                     } : {};
                                    
                                    return (
                                      <ContextMenu key={event.id} onOpenChange={setContextMenuOpen}>
                                        <ContextMenuTrigger asChild>
                                          <div 
                                            className={`p-2 rounded-md border calendar-event-container ${
                                              markedShiftId === event.id
                                                ? 'border-amber-500 border-4 animate-pulse'
                                                : isMultiSelectMode && selectedEvents?.has(event.id) 
                                                ? 'border-primary bg-primary/10' 
                                                : hasMultipleShifts
                                                  ? 'border-purple-500 border-2'
                                                  : 'border-border dark:border-gray-700'
                                             } relative group w-full`}
                                              style={{
                                                backgroundColor: !hasGuards ? '#000000' : (titleColors[event.title]?.backgroundColor || getVibrantShiftColor(event.title).bg),
                                                height: `${MIN_SHIFT_HEIGHT}px`,
                                                maxWidth: "100%",
                                                backgroundImage: !hasGuards ? 
                                                  'repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.15) 5px, rgba(255, 255, 255, 0.1) 5px, rgba(255, 255, 255, 0.1) 10px)' 
                                                  : 'none',
                                                ...diagonalPattern
                                              }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              e.preventDefault();
                                              handleEventClick(event, e);
                                              if (isMultiSelectMode && onEventSelect) {
                                                onEventSelect(event.id);
                                              }
                                            }}
                                           >
                                              {/* Multiple employees watermark - positioned to avoid delete button */}
                                              {multipleEmployees && (
                                                <div 
                                                  className="absolute top-0.5 left-0.5 bg-red-500 text-white rounded-full font-bold shadow-lg z-20 border border-background"
                                                  style={{ 
                                                    fontSize: `${Math.max(8, 10 * (calendarZoom || 1.0))}px`,
                                                    padding: `${Math.max(2, 4 * (calendarZoom || 1.0))}px ${Math.max(4, 6 * (calendarZoom || 1.0))}px`
                                                  }}
                                                >
                                                  {Array.isArray(event.guard_names) ? event.guard_names.filter(name => name && typeof name === 'string' && name.trim() !== '').length : 
                                                   Array.isArray(event.assigned_guards) ? event.assigned_guards.length : 2}
                                                </div>
                                              )}
                                             
                                              {/* Time and duration in a single line with flexbox */}
                                               <div className="font-medium flex items-center justify-between mb-1" 
                                                 style={{
                                                   color: !hasGuards ? '#ffffff' : (titleColors[event.title]?.textColor || getVibrantShiftColor(event.title).text),
                                                   fontSize: `${Math.max(10, 14 * (calendarZoom || 1.0))}px`
                                                 }}>
                                                <div className="flex items-center">
                                                  {!hasGuards ? getShiftIcon(event) : null}
                                                  <span className="truncate">
                                                    {format(fromLocalISOString(event.start_date), 'HH:mm')} - {format(fromLocalISOString(event.end_date), 'HH:mm')}
                                                  </span>
                                                </div>
                                                 {showDuration && (
                                                   <div className="flex items-center gap-1 ml-1 whitespace-nowrap">
                                                     <Clock style={{color: !hasGuards ? '#ffffff' : (titleColors[event.title]?.textColor || getVibrantShiftColor(event.title).text), width: `${Math.max(8, 12 * (calendarZoom || 1.0))}px`, height: `${Math.max(8, 12 * (calendarZoom || 1.0))}px`}} />
                                                     <span>{calculateShiftDuration(event)}</span>
                                                   </div>
                                                 )}
                                              </div>
                                            
                                             {/* Show display text in orange box */}
                                             <div 
                                               className="mt-1 rounded bg-orange-400 text-white font-medium break-words whitespace-normal overflow-hidden text-ellipsis"
                                               style={{ 
                                                 fontSize: `${Math.max(8, 12 * (calendarZoom || 1.0))}px`,
                                                 padding: `${Math.max(2, 4 * (calendarZoom || 1.0))}px ${Math.max(4, 8 * (calendarZoom || 1.0))}px`
                                               }}
                                             >
                                               {displayText}
                                             </div>
                                             
                                             {/* Delete button for individual shifts */}
                                             {permissions.canDelete && (
                                               <button 
                                                 onClick={(e) => {
                                                   e.stopPropagation();
                                                   e.preventDefault();
                                                   onDeleteEvent(event);
                                                 }}
                                                 className="absolute top-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 transition-colors opacity-0 group-hover:opacity-100 z-30"
                                               >
                                                 <Trash2 className="h-3 w-3 text-red-600" />
                                               </button>
                                             )}
                                          </div>
                                        </ContextMenuTrigger>
                                        <ContextMenuContent>
                                          <ContextMenuItem 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleEventClick(event, {} as React.MouseEvent);
                                            }}
                                            disabled={!permissions.canUpdate}
                                          >
                                            <span className="mr-2">✏️</span>
                                            {t('editShift') || 'Edit Shift'}
                                          </ContextMenuItem>
                                          
                                          <ContextMenuItem 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onDuplicateEvent(event);
                                            }}
                                            disabled={!permissions.canCreate}
                                          >
                                            <Copy className="h-4 w-4 mr-2" />
                                            {t('duplicateShift') || 'Duplicate Shift'}
                                          </ContextMenuItem>
                                          
                                          <ContextMenuItem 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onCopyShift(event);
                                            }}
                                          >
                                            <Copy className="h-4 w-4 mr-2" />
                                            {t('copyShift') || 'Copy Shift'}
                                          </ContextMenuItem>
                                          
                                          {permissions.canUpdate && onMarkForSwap && !markedShiftId && (
                                            <ContextMenuItem 
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onMarkForSwap(event);
                                              }}
                                            >
                                              <ArrowLeftRight className="h-4 w-4 mr-2" />
                                              Mark for Swap
                                            </ContextMenuItem>
                                          )}
                                          
                                          {permissions.canUpdate && onSwapWithMarked && markedShiftId && markedShiftId !== event.id && (
                                            <ContextMenuItem 
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onSwapWithMarked(event);
                                              }}
                                              className="font-bold text-amber-600"
                                            >
                                              <ArrowLeftRight className="h-4 w-4 mr-2" />
                                              Swap with Marked Shift
                                            </ContextMenuItem>
                                          )}
                                          
                                          {permissions.canUpdate && onCancelSwap && markedShiftId === event.id && (
                                            <ContextMenuItem 
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onCancelSwap();
                                              }}
                                              className="text-destructive"
                                            >
                                              <X className="h-4 w-4 mr-2" />
                                              Cancel Swap
                                            </ContextMenuItem>
                                          )}
                                          
                                          <ContextMenuSeparator />
                                          
                                          <ContextMenuItem 
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onDeleteEvent(event);
                                            }}
                                            disabled={!permissions.canDelete}
                                            className="text-destructive"
                                          >
                                            <Trash2 className="h-4 w-4 mr-2" />
                                            {t('deleteShift') || 'Delete Shift'}
                                          </ContextMenuItem>
                                        </ContextMenuContent>
                                      </ContextMenu>
                                    );
                                  })
                                ) : (
                                  <div className="text-gray-400 text-xs italic text-center h-14 flex items-center justify-center">
                                    {t('noShifts') || 'No shifts'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </ContextMenuTrigger>
                          <ContextMenuContent>
                            <ContextMenuItem 
                              onClick={() => handleDayContextMenuAction('addShift', daySchedule.date)}
                              disabled={!permissions.canCreate}
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {t('addShift') || 'Add Shift'}
                            </ContextMenuItem>

                            {onPasteShift && (
                              <ContextMenuItem 
                                onClick={() => handleDayContextMenuAction('pasteShift', daySchedule.date)}
                                disabled={!permissions.canUpdate}
                              >
                                <Copy className="h-4 w-4 mr-2" />
                                {t('pasteShift') || 'Paste Shift'}
                              </ContextMenuItem>
                            )}
                            
                            {onCopyDay && eventsForDay.length > 0 && (
                              <>
                                <ContextMenuSeparator />
                                <ContextMenuItem
                                  onClick={() => handleDayContextMenuAction('copyDayWithGuards', daySchedule.date, eventsForDay)}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  {t('copyDayWithGuards') || 'Copy Day (with guards)'}
                                </ContextMenuItem>
                                <ContextMenuItem
                                  onClick={() => handleDayContextMenuAction('copyDayWithoutGuards', daySchedule.date, eventsForDay)}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  {t('copyDayWithoutGuards') || 'Copy Day (without guards)'}
                                </ContextMenuItem>
                              </>
                            )}
                            
                            {onDeleteDay && eventsForDay.length > 0 && (
                              <>
                                <ContextMenuSeparator />
                                <ContextMenuItem
                                  onClick={() => handleDayContextMenuAction('deleteDay', daySchedule.date, eventsForDay)}
                                  disabled={!permissions.canDelete}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {t('deleteDay') || 'Delete Day'}
                                </ContextMenuItem>
                              </>
                            )}
                          </ContextMenuContent>
                        </ContextMenu>
                      );
                    })}
                  </div>
                );
              })}
              
              {/* Add a row at the bottom for adding new shift types */}
              {permissions.canCreate && !isMultiSelectMode && (
                <div className="flex h-16 border-b border-border dark:border-gray-700 w-full">
                  {daySchedules.map((daySchedule, index) => (
                    <div
                      key={`add-shift-${daySchedule.date.toISOString()}`}
                      className={`border-r ${((isMobile && !isMonthView) || isMonthView) ? 'flex-none' : 'flex-1'} ${index === daySchedules.length - 1 ? 'border-r-0' : ''} ${isToday(daySchedule.date) ? 'bg-blue-50/20 dark:bg-blue-900/10' : ''}`}
                      style={getColumnStyle()}
                    >
                      {index === 0 && (
                        <div 
                          className="h-full flex items-center justify-center cursor-pointer hover:bg-muted/50 dark:hover:bg-muted/30 transition-colors" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDayClick(daySchedule.date);
                          }}
                        >
                          <div className="flex items-center gap-2 text-muted-foreground dark:text-gray-300">
                            <Plus className="h-4 w-4" />
                            <span>{t('addNewShiftType') || 'Add New Shift Type'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarRowView;
